// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useDashboardApi } from '../../../store/api/hooks/dashboardApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

const priorityColors: Record<string, { bg: string; color: string }> = {
  'Immediate mortality risk': { bg: '#fee2e2', color: '#dc2626' },
  'Immediate flock performance impact': { bg: '#ffedd5', color: '#ea580c' },
  'Ok to fix between flocks': { bg: '#fefce8', color: '#ca8a04' },
  'Preventative': { bg: '#eff6ff', color: '#2563eb' },
};

const statusColors: Record<string, { bg: string; color: string }> = {
  'Close - Complete': { bg: '#dcfce7', color: '#16a34a' },
  'Close - Incomplete': { bg: '#fee2e2', color: '#dc2626' },
};

function priorityCell(params: any) {
  const val = params.value;
  if (!val) return '';
  const s = priorityColors[val] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{val}</span>;
}

function statusCell(params: any) {
  const val = params.value;
  if (!val) return '';
  const s = statusColors[val] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{val}</span>;
}

function timeToCompleteCell(params: any) {
  const val = params.value;
  if (val == null) return '';
  let color = '#dc2626';
  if (val <= 1) color = '#16a34a';
  else if (val <= 3) color = '#d97706';
  return <span style={{ color, fontWeight: 600 }}>{val} days</span>;
}

function hoursCell(params: any) {
  const val = params.value;
  if (val == null) return '';
  return <span>{Number(val).toLocaleString()} hrs</span>;
}

function checklistCell(params: any) {
  const val = params.value;
  return <span style={{ color: val === 'Yes' ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>{val}</span>;
}

function dateCell(params: any) {
  const val = params.value;
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const ClosedTasksDetailReport: React.FC = () => {
  const dashboardApi = useDashboardApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility) params.FacilityIDs = filterParams.facility;
    if (filterParams.subFacility) params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup) params.AssetGroupIDs = filterParams.assetGroup;
    if (filterParams.department) params.DepartmentIDs = filterParams.department;
    if (filterParams.priority) params.PriorityIDs = filterParams.priority;
    if (filterParams.closedStatus) params.ClosedStatus = filterParams.closedStatus;
    if (filterParams.startDate) params.StartDate = filterParams.startDate;
    if (filterParams.endDate) params.EndDate = filterParams.endDate;

    dashboardApi.getClosedTasksReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.summary?.totalClosed || 0);
        setFilterOptions([
          { key: 'facility', label: 'Facility', options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'subFacility', label: 'Sub Facility', options: (d?.subFacilities || []).filter((f: any) => f.name && f.name.trim()).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'assetGroup', label: 'Asset Group', options: (d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'department', label: 'Department', options: (d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
          { key: 'closedStatus', label: 'Closed Status', options: [{ value: 'Close - Complete', label: 'Close - Complete' }, { value: 'Close - Incomplete', label: 'Close - Incomplete' }] },
          { key: 'priority', label: 'Priority', options: (d?.priorities || []).map((f: any) => ({ value: String(f.id), label: f.name })) },
        ]);
      },
      (error: any) => { console.error('Failed to fetch closed tasks:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>, startDate?: string, endDate?: string) => {
    const newFilters = { ...filterMap, startDate, endDate };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility) params.FacilityIDs = filters.facility;
    if (filters.subFacility) params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup) params.AssetGroupIDs = filters.assetGroup;
    if (filters.department) params.DepartmentIDs = filters.department;
    if (filters.priority) params.PriorityIDs = filters.priority;
    if (filters.closedStatus) params.ClosedStatus = filters.closedStatus;
    if (filters.startDate) params.StartDate = filters.startDate;
    if (filters.endDate) params.EndDate = filters.endDate;
    setIsExporting(true);
    dashboardApi.exportClosedTasksReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'closed_tasks_detail_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    // Task Details
    { headerName: 'Task ID', field: 'taskId', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 180, cellRenderer: titleCell, headerTooltip: 'Task ID' },
    { headerName: 'Task Title', field: 'taskTitle', headerClass: 'bg-[#f0f0f0]', minWidth: 250, flex: 1, cellRenderer: titleCell, headerTooltip: 'Task Title' },
    { headerName: 'Priority', field: 'priorityName', headerClass: 'bg-[#f0f0f0]', width: 220, cellRenderer: priorityCell, headerTooltip: 'Priority' },
    { headerName: 'Task Status', field: 'taskStatus', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: statusCell, headerTooltip: 'Task Status' },
    { headerName: 'Created Date', field: 'createdDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Created Date' },
    { headerName: 'Assigned To', field: 'assignedTo', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Assigned To' },
    { headerName: 'Scheduled Date', field: 'scheduledDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Scheduled Date' },
    { headerName: 'Started Date', field: 'startedDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Started Date' },
    { headerName: 'Closed Date', field: 'closedDate', headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: dateCell, headerTooltip: 'Closed Date' },
    { headerName: 'Time to Complete', field: 'timeToCompleteDays', headerClass: 'bg-[#f0f0f0]', width: 140, type: 'rightAligned', cellRenderer: timeToCompleteCell, headerTooltip: 'Time to Complete (Days)' },
    { headerName: 'Hours Logged', field: 'hoursLogged', headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: hoursCell, headerTooltip: 'Hours Logged' },
    { headerName: 'Checklist', field: 'checklistAttached', headerClass: 'bg-[#f0f0f0]', width: 100, cellRenderer: checklistCell, headerTooltip: 'Checklist Attached' },
    // Work Request Details
    { headerName: 'WR Code', field: 'wrCode', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Work Request Code' },
    { headerName: 'WR Description', field: 'wrDescription', headerClass: 'bg-[#f0f0f0]', minWidth: 250, flex: 1, cellRenderer: titleCell, headerTooltip: 'Work Request Description' },
    { headerName: 'WR Created By', field: 'wrCreatedBy', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'WR Created By' },
    { headerName: 'WR Created Date', field: 'wrCreatedDate', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: dateCell, headerTooltip: 'WR Created Date' },
    { headerName: 'Facility', field: 'facilityName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Name', field: 'assetName', headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
    { headerName: 'Asset Code', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Asset Code' },
    { headerName: 'Asset Group', field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department', field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Department' },
  ];

  return (
    <Layout>
      <Header title="Closed Tasks Detail Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>Total Closed: {summary.totalClosed} tasks</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>Complete: {summary.closedComplete}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>Incomplete: {summary.closedIncomplete}</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#111827', fontWeight: 600 }}>Avg Completion: {summary.avgCompletionDays} days</span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#2B5A2E', fontWeight: 600 }}>Total Hours: {Number(summary.totalHoursLogged || 0).toLocaleString()} hrs</span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </Layout>
  );
};

export default ClosedTasksDetailReport;
